import { schemas } from '@repo/contracts'
import { z } from 'zod'
import { apiErrorHandler } from '../errorHandler'
import {
  conversationBriefSourceSchema as briefSourceSchema,
  conversationBriefValueResponseSchema as briefValueSchema
} from './canonical'

export const conversationProtocolVersion = 1 as const
export const maxConversationFrameBytes = 1024 * 1024

const nonnegativeSafeInteger = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const positiveId = z.number().int().positive().max(Number.MAX_SAFE_INTEGER)
const nonnegativeInt32 = z.number().int().nonnegative().max(2_147_483_647)
const positiveInt32 = z.number().int().positive().max(2_147_483_647)
const optionalPositiveId = positiveId.optional()
const utf8Length = (value: string) => new TextEncoder().encode(value).byteLength
const byteBound = (maximum: number) => z.string().refine((value) => utf8Length(value) <= maximum)
const trimmed = (maximum: number) =>
  z
    .string()
    .min(1)
    .refine((value) => utf8Length(value) <= maximum && value.trim() === value)
const uuid = z.string().uuid()
const nonNilUUID = uuid.refine((value) => value !== '00000000-0000-0000-0000-000000000000')

const briefSlotSchema = schemas.apihttp_ConversationBriefSlotResponse
  .extend({ source: briefSourceSchema.optional(), value: briefValueSchema })
  .strip()
const briefSchema = schemas.apihttp_ConversationBriefResponse
  .extend({
    briefId: positiveId,
    definitionVersion: positiveInt32,
    version: positiveId,
    slots: z.array(briefSlotSchema)
  })
  .strip()
// Re-declared so they strip: `.strip()` binds the object it is called on, not the ones it
// references, so a nested generated schema reached only through a reference keeps its
// `.passthrough()` default and carries unknown keys into client state. See ADR-176.
const computedExcludedCellSchema = schemas.apihttp_ConversationComputedExcludedCellResponse
  .extend({ count: nonnegativeSafeInteger })
  .strip()
const computedTableSchema = schemas.apihttp_ConversationComputedTableResponse
  .extend({
    cellsComputed: nonnegativeSafeInteger,
    rowsMatched: nonnegativeSafeInteger,
    rowsTotal: nonnegativeSafeInteger,
    excludedCells: z.array(computedExcludedCellSchema)
  })
  .strip()
const computedResultSchema = schemas.apihttp_ConversationComputedResultResponse
  .extend({ sourceDocumentIds: z.array(positiveId), table: computedTableSchema.nullish() })
  .strip()
const messageSchema = schemas.apihttp_ConversationSnapshotMessageResponse
  .extend({
    messageId: positiveId,
    createdBy: positiveId,
    createdAtTsMs: nonnegativeSafeInteger,
    updatedAtTsMs: nonnegativeSafeInteger,
    computedResults: z.array(computedResultSchema).optional(),
    proposalId: positiveId.nullish(),
    proposalSectionId: positiveId.nullish(),
    presentationId: positiveId.nullish(),
    presentationSlideId: positiveId.nullish(),
    presentationSlideVersionId: positiveId.nullish()
  })
  .strip()
const stalenessSchema = schemas.apihttp_ConversationStalenessResponse
  .extend({
    messageId: positiveId,
    briefId: positiveId.nullish(),
    linkedVersion: positiveId.nullish(),
    currentVersion: positiveId.nullish()
  })
  .strip()
const documentCandidateSchema = schemas.apihttp_ConversationQuestionCandidateResponse
  .extend({ id: positiveId, label: trimmed(512), secondary: byteBound(512).optional() })
  .strip()
const artifactCandidateSchema = schemas.apihttp_ConversationProposalSectionCandidateResponse
  .extend({
    // Defaulted although the generated contract requires it: a stored event
    // written before M9.23 carried sections only and no type, and the stream
    // replays stored events verbatim. The default restores the field those
    // writers meant, so client state keeps the same required shape.
    type: z.enum(['proposal-section', 'proposal']).default('proposal-section'),
    proposalId: positiveId,
    sectionId: nonnegativeSafeInteger,
    label: trimmed(512),
    secondary: trimmed(512),
    observation: trimmed(2048)
  })
  .strip()
  .superRefine((candidate, context) => {
    // The candidate carries which artifact it names because the answer must
    // round-trip it: a whole proposal names no section, while a section keeps a
    // positive id. An absent type means the older writer above, whose every
    // candidate was a section.
    if (candidate.type === 'proposal') {
      if (candidate.sectionId !== 0) {
        context.addIssue({ code: 'custom', message: 'a whole-proposal candidate names no section' })
      }
      return
    }
    if (candidate.sectionId <= 0) {
      context.addIssue({ code: 'custom', message: 'a proposal-section candidate needs its section' })
    }
  })
const questionResponseSchema = schemas.apihttp_ConversationQuestionResponse
  .extend({
    runId: positiveId,
    briefId: positiveId,
    briefVersion: positiveId,
    ordinal: positiveInt32,
    candidates: z.array(documentCandidateSchema),
    artifactOptions: z.array(artifactCandidateSchema)
  })
  .strip()
const taskSchema = schemas.apihttp_ConversationSnapshotTaskResponse
  .extend({
    originMessageId: positiveId,
    intakeRunId: positiveId,
    briefId: positiveId.nullish(),
    definitionVersion: positiveInt32.nullish(),
    currentBriefVersion: positiveId.nullish(),
    questionRunId: positiveId.optional(),
    executionId: positiveId.nullish(),
    executionRunId: positiveId.optional(),
    executionCreatedAtTsMs: positiveId.nullish(),
    executionSourceMessageId: positiveId.nullable(),
    executionOutputMessageIds: z
      .array(positiveId)
      .max(200)
      .refine((ids) => new Set(ids).size === ids.length, 'execution output message IDs must be unique')
  })
  .strip()

const frameAuthoritySchema = z.object({
  protocolVersion: z.literal(conversationProtocolVersion),
  eventId: nonnegativeSafeInteger,
  projectId: positiveId,
  threadId: positiveId
})

// Retained events written before server time joined protocol v1 still replay, so
// occurrence time is optional at decode. Every newly projected non-snapshot frame
// carries it and the reducer prefers it over receipt time.
const persistedFrameAuthoritySchema = frameAuthoritySchema.extend({ occurredAtTsMs: positiveId.optional() })

const publicStatusSummary = trimmed(160).refine((value) => !/\p{Cc}/u.test(value))

const statusValues = z.enum(['queued', 'running', 'waiting_for_input', 'succeeded', 'failed', 'cancelled'])
const statusOutcomes = z.enum([
  'unsupported-task',
  'capability-unavailable',
  'routed',
  'brief-created',
  'answer-accepted',
  'answer-superseded',
  'budget-exhausted'
])

const snapshotFrameSchema = frameAuthoritySchema
  .extend({
    type: z.literal('snapshot'),
    originKind: z.enum(['native', 'legacy-search', 'legacy-marketing']),
    readOnly: z.boolean(),
    highWaterEventId: nonnegativeSafeInteger,
    tailAfterEventId: nonnegativeSafeInteger,
    rebased: z.boolean(),
    messages: z.array(messageSchema).max(100),
    earliestCursor: z.string().optional(),
    hasOlder: z.boolean(),
    tasks: z.array(taskSchema).max(200),
    briefs: z.array(briefSchema),
    questions: z.array(questionResponseSchema),
    outputs: z.array(stalenessSchema).max(200),
    events: z.array(z.record(z.string(), z.unknown())),
    eventsTruncated: z.boolean()
  })
  .strip()
  .refine(
    (frame) => frame.eventId === frame.highWaterEventId && frame.tailAfterEventId === frame.highWaterEventId,
    'snapshot cursor must equal high water'
  )
  .refine((frame) => frame.originKind !== 'native' || !frame.readOnly, 'native snapshot cannot be cutover read-only')

const statusFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('status'),
    runId: optionalPositiveId,
    briefId: optionalPositiveId,
    executionId: optionalPositiveId,
    status: statusValues.optional(),
    stage: trimmed(128).optional(),
    // The ordered step keys of the execution's graph. The client locates the run by
    // indexing this with stage, so no ordinal or count rides alongside and the graph
    // table stays owned by the backend. Absent on a lifecycle frame, and on any event
    // stored before the trail existed, which the indicator renders as indeterminate.
    steps: z.array(trimmed(128)).max(16).optional(),
    retryable: z.boolean().optional(),
    outcome: statusOutcomes.optional(),
    message: byteBound(1024).optional(),
    summary: publicStatusSummary.optional(),
    capability: byteBound(128).optional(),
    definitionName: byteBound(128).optional(),
    definitionVersion: positiveInt32.optional(),
    questionRunId: optionalPositiveId
  })
  .strip()
  .superRefine((frame, context) => {
    if (frame.status === undefined && frame.outcome === undefined && frame.stage === undefined) {
      context.addIssue({ code: 'custom', message: 'status frame has no state' })
    }
    // Mirrors validateStatusFrameSteps in conversation_stream.go. A stage missing from
    // its own trail indexes to -1, and a repeated key makes the position ambiguous.
    if (frame.steps !== undefined && frame.steps.length > 0) {
      if (new Set(frame.steps).size !== frame.steps.length) {
        context.addIssue({ code: 'custom', message: 'status frame step trail repeats a step' })
      }
      if (frame.stage !== undefined && !frame.steps.includes(frame.stage)) {
        context.addIssue({ code: 'custom', message: 'status frame stage is absent from its own step trail' })
      }
    }
    if (
      frame.outcome === 'capability-unavailable' &&
      (frame.message === undefined || frame.message.trim() !== frame.message || frame.message === '')
    ) {
      context.addIssue({ code: 'custom', message: 'capability-unavailable requires a safe message' })
    }
    if (
      frame.outcome === 'routed' &&
      (frame.capability === undefined ||
        frame.capability === '' ||
        frame.capability.trim() !== frame.capability ||
        frame.definitionVersion === undefined)
    ) {
      context.addIssue({ code: 'custom', message: 'routed requires capability definition' })
    }
  })

const questionFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('question'),
    briefId: positiveId,
    briefVersion: positiveId.optional(),
    runId: positiveId,
    suspensionKey: byteBound(256).optional(),
    ordinal: positiveInt32.optional(),
    questionOrdinal: nonnegativeInt32.optional(),
    // budget-exhausted carries no slot key: it reports that the ask budget ran out
    // across every remaining slot rather than resolving one of them. Every other
    // question frame names its slot, which the superRefine below enforces.
    slotKey: z.union([trimmed(128), z.literal('')]),
    kind: z.enum(['text', 'integer', 'document-reference', 'document-references', 'artifact-reference']).optional(),
    prompt: byteBound(4096).optional(),
    transition: z.enum(['ask', 'accepted', 'superseded', 'budget-exhausted', 'abandoned']).optional(),
    // Named on budget exhaustion so the transcript can say what is still needed. They
    // are registry slot keys, never user content.
    remainingSlotKeys: z.array(trimmed(128)).max(64).optional(),
    candidates: z.array(documentCandidateSchema).max(100).optional(),
    artifactCandidates: z.array(artifactCandidateSchema).max(100).optional(),
    artifactOptions: z.array(artifactCandidateSchema).max(100).optional()
  })
  .strip()
  .superRefine((frame, context) => {
    if (frame.kind === undefined && frame.transition === undefined) {
      context.addIssue({ code: 'custom', message: 'question frame has no descriptor or transition' })
    }
    if (
      frame.kind !== undefined &&
      (frame.ordinal === undefined ||
        frame.suspensionKey === undefined ||
        frame.suspensionKey.trim() !== frame.suspensionKey ||
        frame.suspensionKey === '' ||
        frame.prompt === undefined ||
        frame.prompt.trim() !== frame.prompt ||
        frame.prompt === '' ||
        frame.candidates === undefined ||
        frame.artifactOptions === undefined)
    ) {
      context.addIssue({ code: 'custom', message: 'question descriptor authority is incomplete' })
    }
    if (frame.transition !== undefined && (frame.briefVersion === undefined || frame.questionOrdinal === undefined)) {
      context.addIssue({ code: 'custom', message: 'question transition authority is incomplete' })
    }
    if (frame.slotKey === '' && frame.transition !== 'budget-exhausted' && frame.transition !== 'abandoned') {
      context.addIssue({ code: 'custom', message: 'question frame requires a slot key' })
    }
  })

const briefDeltaFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('brief-delta'),
    briefId: positiveId,
    version: positiveId,
    changedSlotKeys: z.array(z.string()).max(64).optional(),
    complete: z.boolean().optional()
  })
  .strip()

const attemptAuthority = {
  runId: optionalPositiveId,
  briefId: optionalPositiveId,
  executionId: positiveId,
  stepKey: trimmed(128),
  attempt: positiveInt32,
  attemptEpoch: nonNilUUID
}

const contentStartFrameSchema = persistedFrameAuthoritySchema
  .extend({ type: z.literal('content-start'), ...attemptAuthority, reset: z.literal(true) })
  .strip()

const contentDeltaFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('content-delta'),
    ...attemptAuthority,
    sequence: positiveId,
    text: z
      .string()
      .min(1)
      .refine((value) => utf8Length(value) <= 4096)
  })
  .strip()

const committedAuthority = {
  runId: optionalPositiveId,
  briefId: optionalPositiveId,
  executionId: positiveId,
  stepKey: byteBound(128).optional(),
  attempt: positiveInt32.optional(),
  attemptEpoch: nonNilUUID.optional()
}

const contentCommittedFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('content-committed'),
    ...committedAuthority,
    messageId: positiveId.optional(),
    messageIds: z.array(positiveId).max(32).optional()
  })
  .strip()
  .refine(
    (frame) => (frame.messageId !== undefined) !== (frame.messageIds?.length ?? 0) > 0,
    'one message identity is required'
  )
  .refine(
    (frame) => frame.messageIds === undefined || new Set(frame.messageIds).size === frame.messageIds.length,
    'message identities must be unique'
  )

const artifactChangeFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('artifact-change'),
    // A refine started from the canvas writes the artifact with no execution behind it, so
    // the key is absent rather than zero or null. originMessageId rides on every change and
    // names the instruction that caused it, which is the only thing that can place an
    // execution-less one in the transcript.
    executionId: optionalPositiveId,
    originMessageId: positiveId,
    artifactType: z.enum(['proposal', 'proposal-section', 'presentation', 'presentation-slide', 'document']),
    proposalId: optionalPositiveId,
    sectionId: optionalPositiveId,
    presentationId: optionalPositiveId,
    slideId: optionalPositiveId,
    // versionId is the revision a change produced, for a slide and a document alike.
    versionId: optionalPositiveId,
    documentId: optionalPositiveId,
    // The change record: which thing changed and how big it is now. All three are
    // optional because a writer that predates them sends none, and previousWords is
    // absent rather than zero where the producer cannot read the superseded content.
    targetName: trimmed(256).optional(),
    words: nonnegativeSafeInteger.optional(),
    previousWords: nonnegativeSafeInteger.optional()
  })
  .strip()
  .superRefine((frame, context) => {
    const proposal = frame.proposalId !== undefined || frame.sectionId !== undefined
    const presentation = frame.presentationId !== undefined
    const slide = frame.slideId !== undefined
    const document = frame.documentId !== undefined
    const revision = frame.versionId !== undefined
    const valid =
      // A created proposal names itself and nothing else: no section is drafted yet.
      (frame.artifactType === 'proposal' &&
        frame.proposalId !== undefined &&
        frame.sectionId === undefined &&
        !presentation &&
        !slide &&
        !revision &&
        !document) ||
      (frame.artifactType === 'proposal-section' &&
        frame.proposalId !== undefined &&
        frame.sectionId !== undefined &&
        !presentation &&
        !slide &&
        !revision &&
        !document) ||
      (frame.artifactType === 'presentation' && presentation && !proposal && !slide && !revision && !document) ||
      (frame.artifactType === 'presentation-slide' &&
        presentation &&
        frame.slideId !== undefined &&
        frame.versionId !== undefined &&
        !proposal &&
        !document) ||
      // A document change is always a revision of one document.
      (frame.artifactType === 'document' && document && revision && !proposal && !presentation && !slide)
    if (!valid) context.addIssue({ code: 'custom', message: 'artifact identity does not match its type' })
  })

const stalenessFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('staleness'),
    messageId: positiveId,
    state: z.enum(['untracked', 'current', 'stale']),
    reasons: z.array(z.string()).max(32),
    briefId: optionalPositiveId,
    linkedVersion: optionalPositiveId,
    currentVersion: optionalPositiveId
  })
  .strip()

const errorFrameSchema = persistedFrameAuthoritySchema
  .extend({
    type: z.literal('error'),
    code: trimmed(128),
    message: trimmed(1024),
    retryable: z.boolean(),
    runId: optionalPositiveId,
    briefId: optionalPositiveId,
    executionId: optionalPositiveId
  })
  .strip()

export const conversationFrameSchema = z.discriminatedUnion('type', [
  snapshotFrameSchema,
  statusFrameSchema,
  questionFrameSchema,
  briefDeltaFrameSchema,
  contentStartFrameSchema,
  contentDeltaFrameSchema,
  contentCommittedFrameSchema,
  artifactChangeFrameSchema,
  stalenessFrameSchema,
  errorFrameSchema
])

export type ConversationFrame = z.infer<typeof conversationFrameSchema>
export type ConversationSnapshotFrame = Extract<ConversationFrame, { type: 'snapshot' }>

export class ConversationProtocolError extends Error {
  readonly kind: 'invalid' | 'unsupported'

  constructor(kind: 'invalid' | 'unsupported', message: string) {
    super(message)
    this.name = 'ConversationProtocolError'
    this.kind = kind
  }
}

function rejectDuplicateObjectKeys(source: string): void {
  let index = 0
  const skipWhitespace = () => {
    while (/\s/.test(source[index] ?? '')) index += 1
  }
  const readString = (): string => {
    const start = index
    if (source[index] !== '"') throw new Error('expected JSON string')
    index += 1
    while (index < source.length) {
      if (source[index] === '\\') {
        index += 2
        continue
      }
      if (source[index] === '"') {
        index += 1
        return JSON.parse(source.slice(start, index)) as string
      }
      index += 1
    }
    throw new Error('unterminated JSON string')
  }
  const readValue = (): void => {
    skipWhitespace()
    const token = source[index]
    if (token === '{') {
      index += 1
      skipWhitespace()
      const keys = new Set<string>()
      if (source[index] === '}') {
        index += 1
        return
      }
      while (index < source.length) {
        skipWhitespace()
        const key = readString()
        if (keys.has(key)) throw new Error(`duplicate JSON field ${key}`)
        keys.add(key)
        skipWhitespace()
        if (source[index] !== ':') throw new Error('expected JSON colon')
        index += 1
        readValue()
        skipWhitespace()
        if (source[index] === '}') {
          index += 1
          return
        }
        if (source[index] !== ',') throw new Error('expected JSON object separator')
        index += 1
      }
      throw new Error('unterminated JSON object')
    }
    if (token === '[') {
      index += 1
      skipWhitespace()
      if (source[index] === ']') {
        index += 1
        return
      }
      while (index < source.length) {
        readValue()
        skipWhitespace()
        if (source[index] === ']') {
          index += 1
          return
        }
        if (source[index] !== ',') throw new Error('expected JSON array separator')
        index += 1
      }
      throw new Error('unterminated JSON array')
    }
    if (token === '"') {
      readString()
      return
    }
    const start = index
    while (index < source.length && !/[\s,\]}]/.test(source[index] ?? '')) index += 1
    if (start === index) throw new Error('expected JSON value')
    JSON.parse(source.slice(start, index))
  }

  readValue()
  skipWhitespace()
  if (index !== source.length) throw new Error('trailing JSON content')
}

export function decodeConversationFrame(value: unknown): ConversationFrame {
  if (typeof value === 'string') {
    if (utf8Length(value) > maxConversationFrameBytes) {
      throw new ConversationProtocolError('invalid', 'conversation frame is too large')
    }
    try {
      rejectDuplicateObjectKeys(value)
      value = JSON.parse(value) as unknown
    } catch {
      throw new ConversationProtocolError('invalid', 'conversation frame is not valid JSON')
    }
  } else {
    let encoded: string
    try {
      encoded = JSON.stringify(value)
    } catch {
      throw new ConversationProtocolError('invalid', 'conversation frame is not serialisable')
    }
    if (utf8Length(encoded) > maxConversationFrameBytes) {
      throw new ConversationProtocolError('invalid', 'conversation frame is too large')
    }
  }
  if (typeof value === 'object' && value !== null && 'protocolVersion' in value && value.protocolVersion !== 1) {
    throw new ConversationProtocolError('unsupported', 'unsupported conversation protocol version')
  }
  const result = conversationFrameSchema.safeParse(value)
  if (!result.success) throw new ConversationProtocolError('invalid', 'invalid conversation frame')
  if (result.data.type === 'snapshot') {
    let previousEventId = -1
    for (const event of result.data.events) {
      const embedded = decodeConversationFrame(event)
      if (
        embedded.type === 'snapshot' ||
        embedded.projectId !== result.data.projectId ||
        embedded.threadId !== result.data.threadId ||
        embedded.eventId > result.data.highWaterEventId ||
        embedded.eventId <= previousEventId
      ) {
        throw new ConversationProtocolError('invalid', 'invalid embedded snapshot event authority')
      }
      previousEventId = embedded.eventId
    }
  }
  return result.data
}

export function conversationSnapshotToFrame(
  projectId: number,
  snapshot: Omit<ConversationSnapshotFrame, 'type' | 'eventId' | 'projectId' | 'protocolVersion'> & {
    protocolVersion: number
  }
): ConversationSnapshotFrame {
  const frame = decodeConversationFrame({
    ...snapshot,
    type: 'snapshot',
    eventId: snapshot.highWaterEventId,
    projectId
  })
  if (frame.type !== 'snapshot') throw new ConversationProtocolError('invalid', 'conversation snapshot type mismatch')
  return frame
}

type SSERecord = { event: string; id?: string; data: string }

function parseSSERecord(block: string): SSERecord | undefined {
  let event = 'message'
  let id: string | undefined
  const data: Array<string> = []
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith(':')) continue
    const separator = line.indexOf(':')
    const field = separator < 0 ? line : line.slice(0, separator)
    let value = separator < 0 ? '' : line.slice(separator + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'event') event = value
    if (field === 'id') id = value
    if (field === 'data') data.push(value)
  }
  if (data.length === 0) return undefined
  return { event, ...(id === undefined ? {} : { id }), data: data.join('\n') }
}

function decodeSSERecord(record: SSERecord): ConversationFrame | undefined {
  if (record.event !== 'conversation') return undefined
  const frame = decodeConversationFrame(record.data)
  if (record.id !== undefined && record.id !== String(frame.eventId)) {
    throw new ConversationProtocolError('invalid', 'SSE event identity does not match payload')
  }
  return frame
}

function consumeCompleteSSERecords(buffer: string, onFrame: (frame: ConversationFrame) => void): string {
  let boundary = buffer.search(/\r?\n\r?\n/)
  while (boundary >= 0) {
    const block = buffer.slice(0, boundary)
    const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? '\n\n'
    buffer = buffer.slice(boundary + separator.length)
    const record = parseSSERecord(block)
    if (record !== undefined) {
      const frame = decodeSSERecord(record)
      if (frame !== undefined) onFrame(frame)
    }
    boundary = buffer.search(/\r?\n\r?\n/)
  }
  return buffer
}

export function parseConversationSSE(raw: string): Array<ConversationFrame> {
  const frames: Array<ConversationFrame> = []
  for (const block of raw.split(/\r?\n\r?\n/)) {
    const record = parseSSERecord(block)
    if (record === undefined) continue
    const frame = decodeSSERecord(record)
    if (frame !== undefined) frames.push(frame)
  }
  return frames
}

type StreamClientOptions = {
  projectId: number
  conversationId: number
  fetcher?: typeof fetch
  onFrame: (frame: ConversationFrame) => void
  rebootstrap: () => void | Promise<void>
  onUnsupported: (error: ConversationProtocolError) => void
  recoveryMarker?: ConversationStreamRecoveryMarker
}

export type ConversationStreamRecoveryPersistence = {
  read: () => boolean
  write: (rebootstrapUsed: boolean) => void
}

export type ConversationStreamRecoveryMarker = {
  isRebootstrapUsed: () => boolean
  markRebootstrapUsed: () => void
}

export function createConversationStreamRecoveryMarker(
  persistence?: ConversationStreamRecoveryPersistence
): ConversationStreamRecoveryMarker {
  let inMemory = false
  return {
    isRebootstrapUsed: () => persistence?.read() ?? inMemory,
    markRebootstrapUsed: () => {
      persistence?.write(true)
      inMemory = true
    }
  }
}

function browserRecoveryPersistence(
  projectId: number,
  conversationId: number
): ConversationStreamRecoveryPersistence | undefined {
  if (typeof window === 'undefined') return undefined
  const key = `snowvault:conversation:rebootstrap:${projectId}:${conversationId}`
  return {
    read: () => window.sessionStorage.getItem(key) === 'used',
    write: (rebootstrapUsed) => {
      if (rebootstrapUsed) window.sessionStorage.setItem(key, 'used')
      else window.sessionStorage.removeItem(key)
    }
  }
}

export type ConversationStreamClient = {
  connect: (lastEventId?: number) => Promise<void>
  close: () => void
}

export function createConversationStreamClient(options: StreamClientOptions): ConversationStreamClient {
  if (!Number.isSafeInteger(options.projectId) || options.projectId <= 0) {
    throw new ConversationProtocolError('invalid', 'projectId must be a positive integer')
  }
  if (!Number.isSafeInteger(options.conversationId) || options.conversationId <= 0) {
    throw new ConversationProtocolError('invalid', 'conversationId must be a positive integer')
  }
  const fetcher = options.fetcher ?? fetch
  let controller: AbortController | undefined
  const recoveryMarker =
    options.recoveryMarker ??
    createConversationStreamRecoveryMarker(browserRecoveryPersistence(options.projectId, options.conversationId))

  const recover = async (error: ConversationProtocolError) => {
    try {
      if (!recoveryMarker.isRebootstrapUsed()) {
        recoveryMarker.markRebootstrapUsed()
        await options.rebootstrap()
        return
      }
    } catch {
      options.onUnsupported(new ConversationProtocolError('unsupported', 'conversation recovery state is unavailable'))
      return
    }
    options.onUnsupported(error)
  }

  return {
    async connect(lastEventId?: number) {
      if (lastEventId !== undefined && (!Number.isSafeInteger(lastEventId) || lastEventId <= 0)) {
        throw new ConversationProtocolError('invalid', 'Last-Event-ID must be a positive integer')
      }
      controller?.abort()
      const connectionController = new AbortController()
      controller = connectionController
      let authoritativeThreadId: number | undefined
      const acceptFrame = (frame: ConversationFrame) => {
        if (authoritativeThreadId === undefined) {
          if (frame.type !== 'snapshot') {
            throw new ConversationProtocolError('invalid', 'conversation stream must begin with a snapshot')
          }
          if (frame.projectId !== options.projectId) {
            throw new ConversationProtocolError('invalid', 'conversation snapshot project does not match the request')
          }
          if (frame.threadId !== options.conversationId) {
            throw new ConversationProtocolError('invalid', 'conversation snapshot chat does not match the request')
          }
          authoritativeThreadId = frame.threadId
        } else if (frame.projectId !== options.projectId || frame.threadId !== authoritativeThreadId) {
          throw new ConversationProtocolError('invalid', 'conversation tail scope does not match the snapshot')
        }
        options.onFrame(frame)
      }
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        'X-Conversation-Protocol-Version': String(conversationProtocolVersion)
      }
      if (lastEventId !== undefined) headers['Last-Event-ID'] = String(lastEventId)
      try {
        const streamPath = `/api/projects/${options.projectId}/conversations/${options.conversationId}/stream`
        const response = await fetcher(streamPath, {
          method: 'GET',
          credentials: 'same-origin',
          headers,
          signal: connectionController.signal
        })
        await apiErrorHandler(response)
        if (response.headers.get('X-Conversation-Protocol-Version') !== String(conversationProtocolVersion)) {
          await response.body?.cancel()
          throw new ConversationProtocolError('unsupported', 'unsupported conversation protocol response')
        }
        if (!response.headers.get('Content-Type')?.toLowerCase().startsWith('text/event-stream')) {
          await response.body?.cancel()
          throw new ConversationProtocolError('invalid', 'conversation response is not an event stream')
        }
        const body = response.body
        if (body === null) throw new ConversationProtocolError('invalid', 'conversation response has no body')
        const decoder = new TextDecoder('utf-8', { fatal: true })
        let buffer = ''
        for await (const chunk of body) {
          try {
            buffer += decoder.decode(chunk, { stream: true })
          } catch {
            throw new ConversationProtocolError('invalid', 'conversation stream is not valid UTF-8')
          }
          buffer = consumeCompleteSSERecords(buffer, acceptFrame)
          if (utf8Length(buffer) > maxConversationFrameBytes) {
            throw new ConversationProtocolError('invalid', 'conversation event is too large')
          }
        }
        try {
          buffer += decoder.decode()
        } catch {
          throw new ConversationProtocolError('invalid', 'conversation stream is not valid UTF-8')
        }
        buffer = consumeCompleteSSERecords(buffer, acceptFrame)
        if (buffer.trim() !== '') throw new ConversationProtocolError('invalid', 'truncated conversation event')
        if (authoritativeThreadId === undefined) {
          throw new ConversationProtocolError('invalid', 'conversation stream has no authoritative snapshot')
        }
      } catch (error) {
        if (error instanceof ConversationProtocolError) {
          await recover(error)
          return
        }
        if (connectionController.signal.aborted) return
        throw error
      } finally {
        if (controller === connectionController) controller = undefined
      }
    },
    close() {
      controller?.abort()
      controller = undefined
    }
  }
}
